'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FaqZigzag from './components/FaqZigzag';
import CtaBlock from './components/CtaBlock';
import ServicesSection from './components/ServicesSection';
import PhotoQuoteForm from './components/PhotoQuoteForm';
import BookingModal from './components/BookingModal';
import StickyMobileBar from './components/StickyMobileBar';
import Icon from '@/components/ui/AppIcon';

// Images in reverse order: 4th uploaded → 1st displayed, 3rd → 2nd, 2nd → 3rd, 1st → 4th
// Uploaded order: [0]=logo, [1]=IMG_8097, [2]=IMG_8103, [3]=IMG_8102, [4]=IMG_8094
// Reverse: slot1=IMG_8094, slot2=IMG_8102, slot3=IMG_8103, slot4=IMG_8097
const faqItems = [
  {
    question: 'How long does a detail usually take?',
    answer: (
      <div className="space-y-3">
        <p>The time depends on the size of the vehicle and its condition. Most standard details take anywhere from 2–5 hours. Vehicles with excessive dirt, pet hair, stains, or neglected paint may require additional time to make sure everything is cleaned properly and finished to a high standard.</p>
        <p>Our goal is quality over speed — we take the time needed to leave your vehicle looking its best instead of rushing through the job.</p>
      </div>
    ),
    imageSrc: '/assets/images/IMG_8094-1779432418879.jpeg',
    imageAlt: 'Professional car detailing in progress showing thorough cleaning process',
    tag: 'Timing',
    microCta: 'Book Your Detail',
  },
  {
    question: 'Do you come to my house or do I have to drop the car off?',
    answer: (
      <div className="space-y-3">
        <p>We are a fully mobile detailing service, meaning we come directly to your home, apartment, or workplace for maximum convenience. You can relax inside while we handle the detail on-site.</p>
        <p>We bring the professional equipment and products needed to complete the job, making the process easy and stress-free for you.</p>
      </div>
    ),
    imageSrc: '/assets/images/IMG_8102-1779432418872.jpeg',
    imageAlt: 'Mobile detailing setup at a residential location with professional equipment',
    tag: 'Mobile Service',
    microCta: 'Schedule a Visit',
  },
  {
    question: 'What is included in an interior detail?',
    answer: (
      <div className="space-y-3">
        <p>Our interior detail is designed to fully refresh and restore your vehicle&apos;s cabin. Services typically include:</p>
        <ul className="space-y-1.5 pl-1">
          {[
            'Full vacuum of carpets, seats, trunk, and floor mats',
            'Deep cleaning of interior surfaces',
            'Wipe down and sanitizing of plastics, dashboard, doors, and console',
            'Window and mirror cleaning',
            'Seat and carpet cleaning as needed',
            'Removal of dust, dirt, and buildup from hard-to-reach areas',
          ]?.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span style={{ color: '#2D5F9A', marginTop: '2px' }}>✦</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p>We focus on making the interior look clean, feel fresh, and restore that &quot;new car&quot; feeling.</p>
      </div>
    ),
    imageSrc: '/assets/images/IMG_8103-1779432418864.jpeg',
    imageAlt: 'Clean and detailed car interior showing spotless seats and dashboard',
    tag: 'Interior Detail',
    microCta: 'Get Interior Detail',
  },
  {
    question: 'My car is already pretty clean. Do I still need a detail?',
    answer: (
      <div className="space-y-3">
        <p>Absolutely. Even newer or cleaner vehicles benefit from regular detailing. Dirt, dust, oils, road grime, and contaminants slowly build up over time, even if the car looks clean on the surface.</p>
        <p>A maintenance detail helps:</p>
        <ul className="space-y-1.5 pl-1">
          {[
            'Preserve your paint and interior condition',
            'Maintain resale value',
            'Keep the vehicle protected long term',
            'Prevent buildup that becomes harder to clean later',
          ]?.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span style={{ color: '#2D5F9A', marginTop: '2px' }}>✦</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p>If your vehicle is already in good condition, we can recommend a lighter refresh detail instead of a full restoration service.</p>
      </div>
    ),
    imageSrc: '/assets/images/IMG_8097-1779432418877.jpeg',
    imageAlt: 'Pristine detailed car exterior showing glossy paint and clean finish',
    tag: 'Maintenance',
    microCta: 'Book a Refresh Detail',
  },
];

const whyChooseUs = [
  {
    icon: '🚗',
    title: 'Mobile Convenience',
    desc: 'We come to your home, apartment, or workplace. No drop-off, no waiting rooms.',
  },
  {
    icon: '✨',
    title: 'Professional-Grade Products',
    desc: 'We use premium detailing products and equipment for a showroom-quality finish.',
  },
  {
    icon: '🎯',
    title: 'Attention to Detail',
    desc: 'Every inch of your vehicle is treated with care. We don\'t cut corners.',
  },
  {
    icon: '⭐',
    title: 'Customer Satisfaction',
    desc: 'Your satisfaction is our priority. We\'re not done until you\'re happy.',
  },
];

export default function HomePage() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: '#1C1C1E' }}>
      <Header />
      <main>
        {/* ── HERO SECTION ── */}
        <section
          id="hero"
          className="relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #0a0a0b 0%, #111113 50%, #1C1C1E 100%)', minHeight: '90vh' }}
        >
          {/* Background texture */}
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, #2D5F9A 0%, transparent 50%), radial-gradient(circle at 80% 20%, #2D5F9A 0%, transparent 40%)',
            }}
          />

          <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 pt-20 pb-24 md:pt-28 md:pb-32">
            <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
              {/* Left: text */}
              <div className="flex-1 flex flex-col gap-6 text-center md:text-left">
                {/* Logo */}
                <div className="flex items-center gap-4 justify-center md:justify-start">
                  <Image
                    src="/assets/images/ChatGPT_Image_May_22_2026_01_33_45_AM-1779432418643.png"
                    alt="Lotus Line Detailing Logo"
                    width={72}
                    height={72}
                    className="rounded-full object-cover"
                    style={{ border: '2px solid rgba(45,95,154,0.4)', boxShadow: '0 0 30px rgba(45,95,154,0.2)' }}
                  />
                  <div className="text-left">
                    <p className="mono text-[10px] tracking-widest uppercase" style={{ color: '#2D5F9A' }}>
                      Fully Mobile · Premium Service
                    </p>
                    <h1 className="text-2xl md:text-3xl font-bold" style={{ color: '#F5F5F7' }}>
                      Lotus Line Detailing
                    </h1>
                  </div>
                </div>

                <div>
                  <h2
                    className="text-4xl md:text-6xl font-bold tracking-tight leading-none mb-4"
                    style={{ color: '#F5F5F7' }}
                  >
                    Your Car Deserves<br />
                    <span
                      style={{
                        background: 'linear-gradient(90deg, #2D5F9A, #5B8FCC)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      The Best.
                    </span>
                  </h2>
                  <p className="text-base md:text-lg leading-relaxed max-w-lg" style={{ color: 'rgba(168,169,173,0.8)' }}>
                    Professional mobile detailing that comes to you. We bring the equipment, the expertise, and the results — right to your driveway.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  <button
                    onClick={() => setModalOpen(true)}
                    className="btn-sapphire flex items-center justify-center gap-3 px-8 py-4 rounded-full mono text-[12px] font-semibold tracking-widest uppercase text-white"
                  >
                    <Icon name="CalendarDaysIcon" size={16} variant="outline" />
                    Book Now
                  </button>
                  <a
                    href="#pricing"
                    className="flex items-center justify-center gap-2 px-8 py-4 rounded-full mono text-[12px] font-semibold tracking-widest uppercase transition-all duration-300"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(212,212,216,0.2)',
                      color: 'rgba(212,212,216,0.8)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(45,95,154,0.5)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(212,212,216,0.2)')}
                  >
                    View Pricing
                  </a>
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap gap-4 justify-center md:justify-start mt-2">
                  {['✦ Mobile Service', '◈ Premium Products', '★ 5-Star Rated', '◉ Satisfaction Guaranteed']?.map((badge) => (
                    <span
                      key={badge}
                      className="mono text-[9px] font-medium tracking-widest uppercase"
                      style={{ color: 'rgba(168,169,173,0.4)' }}
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </div>

              {/* Right: hero image */}
              <div className="flex-1 w-full max-w-lg">
                <div
                  className="relative overflow-hidden rounded-2xl"
                  style={{
                    border: '1px solid rgba(45,95,154,0.25)',
                    boxShadow: '0 0 60px rgba(45,95,154,0.15)',
                    aspectRatio: '4/3',
                  }}
                >
                  <Image
                    src="/assets/images/IMG_8094-1779432418879.jpeg"
                    alt="Lotus Line Detailing professional car detail result"
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    priority
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%)' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {/* ── WHY CHOOSE US ── */}
          <section id="why" className="py-20 md:py-28">
            <div className="mb-12">
              <p className="mono text-[10px] tracking-widest uppercase mb-3" style={{ color: '#2D5F9A' }}>
                Why Choose Us
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-off-white">
                The Lotus Line Difference
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
              {whyChooseUs?.map((item) => (
                <div
                  key={item?.title}
                  className="rounded-2xl p-6 flex flex-col gap-3"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(212,212,216,0.08)',
                  }}
                >
                  <span className="text-3xl">{item?.icon}</span>
                  <h3 className="text-base font-semibold" style={{ color: '#F5F5F7' }}>{item?.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(168,169,173,0.65)' }}>{item?.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── SERVICES & PRICING ── */}
          <ServicesSection onBook={() => setModalOpen(true)} />

          {/* ── FAQ SECTION ── */}
          <div id="faq" className="pt-8 pb-4">
            <div className="chrome-divider mb-12" />
            <p className="mono text-[10px] tracking-widest uppercase mb-3" style={{ color: '#2D5F9A' }}>
              Frequently Asked Questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-off-white tracking-tight mb-2">
              Your Questions,<br />
              <span style={{ color: '#A8A9AD', fontWeight: 300 }}>Answered Honestly.</span>
            </h2>
          </div>

          <div className="chrome-divider mb-2" />

          {faqItems?.map((item, i) => (
            <React.Fragment key={i}>
              <FaqZigzag item={item} index={i} onCta={() => setModalOpen(true)} />
              {i < faqItems?.length - 1 && <div className="chrome-divider my-2" />}
            </React.Fragment>
          ))}

          {/* ── CONTACT / BOOKING ── */}
          <section id="contact" className="py-20 md:py-28">
            <div className="chrome-divider mb-12" />
            <div className="grid md:grid-cols-2 gap-10">
              {/* Contact info */}
              <div className="flex flex-col gap-6">
                <div>
                  <p className="mono text-[10px] tracking-widest uppercase mb-3" style={{ color: '#2D5F9A' }}>
                    Get In Touch
                  </p>
                  <h2 className="text-3xl md:text-4xl font-bold text-off-white tracking-tight">
                    Ready to Book?<br />
                    <span style={{ color: '#A8A9AD', fontWeight: 300 }}>We&apos;re Ready for You.</span>
                  </h2>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(168,169,173,0.7)' }}>
                  Reach out via phone or Instagram to schedule your detail. We serve the Chicagoland area and surrounding neighborhoods.
                </p>
                <div className="flex flex-col gap-4">
                  <a
                    href="tel:+13313566014"
                    className="flex items-center gap-3 px-5 py-4 rounded-xl transition-all duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(212,212,216,0.1)',
                      color: '#F5F5F7',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(45,95,154,0.4)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(212,212,216,0.1)')}
                  >
                    <Icon name="PhoneIcon" size={18} variant="outline" className="text-sapphire" />
                    <div>
                      <p className="mono text-[9px] tracking-widest uppercase mb-0.5" style={{ color: 'rgba(168,169,173,0.45)' }}>Phone</p>
                      <p className="text-sm font-medium">331-356-6014</p>
                    </div>
                  </a>
                  <a
                    href="tel:+18478493854"
                    className="flex items-center gap-3 px-5 py-4 rounded-xl transition-all duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(212,212,216,0.1)',
                      color: '#F5F5F7',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(45,95,154,0.4)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(212,212,216,0.1)')}
                  >
                    <Icon name="PhoneIcon" size={18} variant="outline" className="text-sapphire" />
                    <div>
                      <p className="mono text-[9px] tracking-widest uppercase mb-0.5" style={{ color: 'rgba(168,169,173,0.45)' }}>Phone</p>
                      <p className="text-sm font-medium">847-849-3854</p>
                    </div>
                  </a>
                  <a
                    href="https://instagram.com/lotuslinedetailing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-5 py-4 rounded-xl transition-all duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(212,212,216,0.1)',
                      color: '#F5F5F7',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(45,95,154,0.4)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(212,212,216,0.1)')}
                  >
                    <Icon name="CameraIcon" size={18} variant="outline" className="text-sapphire" />
                    <div>
                      <p className="mono text-[9px] tracking-widest uppercase mb-0.5" style={{ color: 'rgba(168,169,173,0.45)' }}>Instagram</p>
                      <p className="text-sm font-medium">@lotuslinedetailing</p>
                    </div>
                  </a>
                  <div
                    className="flex items-center gap-3 px-5 py-4 rounded-xl"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(212,212,216,0.1)',
                    }}
                  >
                    <Icon name="MapPinIcon" size={18} variant="outline" className="text-sapphire" />
                    <div>
                      <p className="mono text-[9px] tracking-widest uppercase mb-0.5" style={{ color: 'rgba(168,169,173,0.45)' }}>Service Area</p>
                      <p className="text-sm font-medium" style={{ color: '#F5F5F7' }}>Chicagoland Area & Surrounding Neighborhoods</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking form */}
              <div
                className="rounded-2xl p-7"
                style={{ background: '#111113', border: '1px solid rgba(212,212,216,0.09)' }}
              >
                <p className="mono text-[10px] tracking-widest uppercase mb-5" style={{ color: '#2D5F9A' }}>
                  Quick Booking Request
                </p>
                <PhotoQuoteForm />
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <CtaBlock onBook={() => setModalOpen(true)} label="Book Your Detail Today" />

          {/* Trust footer strip */}
          <div
            className="mb-16 rounded-xl px-6 md:px-10 py-6 flex flex-wrap items-center justify-center gap-6 md:gap-10"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(212,212,216,0.07)',
            }}
          >
            {[
              '✦ Fully Mobile Service',
              '◈ Premium Products',
              '★ 5-Star Rated',
              '◉ Satisfaction Guaranteed',
              '⬡ Locally Owned & Operated',
            ]?.map((badge) => (
              <span
                key={badge}
                className="mono text-[10px] font-medium tracking-widest uppercase"
                style={{ color: 'rgba(168,169,173,0.45)' }}
              >
                {badge}
              </span>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <BookingModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      <StickyMobileBar onBook={() => setModalOpen(true)} />
    </div>
  );
}
